import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles"

const PROFILE =
  "https://www.realself.com/dr/callum-faris-amsterdam-netherlands#dr-reviews"

const useStyles = makeStyles({
  frame: {
    display: "block",
    width: "100%",
    border: 0,
    background: "#fff",
    borderRadius: 8,
  },
  status: { color: "#cfcccf", margin: "0 0 16px", lineHeight: 1.6 },
  actions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 24,
    marginTop: 20,
  },
  link: {
    color: "#C9A24B",
    textUnderlineOffset: 5,
    "&:focus-visible": { outline: "2px solid #C9A24B", outlineOffset: 5 },
  },
  retry: {
    font: "inherit",
    cursor: "pointer",
    color: "#fff",
    border: "1px solid #777",
    borderRadius: 999,
    padding: "12px 24px",
    background: "transparent",
    "&:focus-visible": { outline: "2px solid #C9A24B", outlineOffset: 3 },
  },
})

export default function RealSelfReviews({ locale }) {
  const classes = useStyles()
  const frame = useRef(null)
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState("loading")
  const [height, setHeight] = useState(640)
  const en = locale === "en"

  useEffect(() => {
    setStatus("loading")
    setHeight(640)
    // RealSelf's script only initialises on a fresh page load, so every mount,
    // retry and language change loads a new iframe document.
    const timeout = window.setTimeout(() => setStatus("unavailable"), 15000)
    const receive = (event) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== frame.current?.contentWindow
      )
        return
      if (event.data?.type !== "face-realself") return
      if (event.data.status === "ready") {
        window.clearTimeout(timeout)
        setStatus("ready")
        if (Number.isFinite(event.data.height) && event.data.height > 0) {
          setHeight(Math.max(240, Math.min(12000, event.data.height)))
        }
      } else if (event.data.status === "error") {
        window.clearTimeout(timeout)
        setStatus("unavailable")
      }
    }
    window.addEventListener("message", receive)
    // Set src after adding the listener, or a cached load can report first.
    frame.current.src = `/embeds/realself.html?lang=${locale}&attempt=${attempt}`
    return () => {
      window.clearTimeout(timeout)
      window.removeEventListener("message", receive)
    }
  }, [locale, attempt])

  return (
    <div>
      {status !== "ready" && (
        <p className={classes.status} role="status">
          {status === "loading"
            ? en
              ? "Loading RealSelf reviews…"
              : "RealSelf-reviews worden geladen…"
            : en
            ? "RealSelf could not load. Try again or read the reviews on RealSelf."
            : "RealSelf kon niet worden geladen. Probeer opnieuw of lees de reviews op RealSelf."}
        </p>
      )}
      <iframe
        key={`${locale}-${attempt}`}
        ref={frame}
        title={
          en
            ? "RealSelf patient reviews — facelift selected"
            : "Patiëntervaringen op RealSelf — facelift geselecteerd"
        }
        className={classes.frame}
        style={{ height, display: status === "unavailable" ? "none" : "block" }}
      />
      <div className={classes.actions}>
        <a
          className={classes.link}
          href={PROFILE}
          target="_blank"
          rel="noopener noreferrer"
        >
          {en ? "Read on RealSelf" : "Lees op RealSelf"}{" "}
          <span aria-hidden="true">↗</span>
        </a>
        {status === "unavailable" && (
          <button
            className={classes.retry}
            onClick={() => setAttempt((value) => value + 1)}
          >
            {en ? "Try again" : "Opnieuw proberen"}
          </button>
        )}
      </div>
    </div>
  )
}

RealSelfReviews.propTypes = { locale: PropTypes.oneOf(["nl", "en"]).isRequired }
